import React from 'react';
import { useInView } from '@/shared/lib/use-in-view';

const STAGGER_MS = 60;
const MAX_DELAY_MS = 360;

type RevealProps = React.ComponentProps<'div'> & {
  /** Position in a staggered group; delays the reveal by 60ms per step. */
  index?: number;
};

/**
 * Scroll-reveal wrapper: fades/slides children in the first time they SCROLL
 * into the viewport (styles live on `[data-reveal]` in styles.css). Content
 * already visible at mount renders instantly with no animation — route
 * entrances belong to the view transition, not to Reveal.
 */
export function Reveal({ index = 0, style, children, ...props }: RevealProps) {
  const { ref, inView, animate } = useInView<HTMLDivElement>();
  const delay = Math.min(index * STAGGER_MS, MAX_DELAY_MS);

  if (inView && !animate) {
    return (
      <div ref={ref} style={style} {...props}>
        {children}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      data-reveal={inView ? 'visible' : 'hidden'}
      style={delay > 0 ? { transitionDelay: `${delay}ms`, ...style } : style}
      {...props}
    >
      {children}
    </div>
  );
}
